import { storeToRefs } from "pinia";

import { dateToTimestamp, getCurrentTimestamp } from "shared/helpers/date";
import ViewedItem from "shared/resources/ViewedItem";
import { useFeatureTeasersStore } from "shared/stores/featureTeasers";
import { useUserStore } from "shared/stores/user";
import { type FeatureKey } from "shared/types/features";

const THIRTY_DAYS_IN_SECONDS = 24 * 3600 * 30;

interface UseFeatureTeasersReturn {
  createViewedItemForFeature: (featureName: FeatureKey) => Promise<void>;
  viewedFeatureTeaser: (featureName: FeatureKey) => boolean;
}

export default function useFeatureTeasers(): UseFeatureTeasersReturn {
  const { currentUser } = storeToRefs(useUserStore());

  const featureTeasersStore = useFeatureTeasersStore();

  const { fetchFeatureTeasers, findFeature, findFeatureId } =
    featureTeasersStore;

  const { featureTeasers } = storeToRefs(featureTeasersStore);

  function viewedFeatureTeaser(featureName: string): boolean {
    if (featureTeasers.value.length === 0) return true;

    const feature = findFeature(featureName);

    if (!feature) return true;

    if (feature.viewedItems.length === 0) return true;

    const viewedItem = feature.viewedItems[0];

    const timestamp = getCurrentTimestamp();
    const thirtyDaysAgoTimestamp = timestamp - THIRTY_DAYS_IN_SECONDS;

    return dateToTimestamp(viewedItem.viewedAt) < thirtyDaysAgoTimestamp;
  }

  async function createViewedItemForFeature(
    featureName: FeatureKey
  ): Promise<void> {
    if (viewedFeatureTeaser(featureName)) {
      const featureId = findFeatureId(featureName);

      const viewedItem = new ViewedItem({
        targetId: featureId,
        targetType: "FeatureTeaser",
        userId: currentUser.value.id,
        viewedAt: new Date().toISOString(),
      });

      const createViewedItemPromises = [
        viewedItem.saveResource(),
        fetchFeatureTeasers(),
      ];

      await Promise.all(createViewedItemPromises);
    }
  }

  return {
    createViewedItemForFeature,
    viewedFeatureTeaser,
  };
}
